import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, ReplaySubject, Subject } from 'rxjs';
import { map, switchMap, takeUntil, tap } from 'rxjs/operators';
import {
  EasBuilderForm,
  EasBuilderSecureForm,
  EasBuilderState,
  EasBuilderTransition,
  EasFormWFGroup,
  EasWorkflowContext,
  FieldPermission
} from './builder.interfaces';
import { WorkflowService } from './workflow.service';
import { BuilderFormService } from './form.service';
import { User } from '@fry/lib/users';
import { EasFormGroup, EasFormService } from '../easform';


@Injectable()
export class BuilderStateService implements OnDestroy {

  constructor(
    private workflow: WorkflowService,
    private formService: BuilderFormService,
    private easForm: EasFormService,
  ) {
  }

  public initialised = false;

  private _easFormGroup$ = new ReplaySubject<EasFormGroup>(1);
  private easFormGroup: EasFormGroup;
  public easFormGroup$ = this._easFormGroup$.asObservable();

  private _transitions$ = new ReplaySubject<EasBuilderTransition[]>(1);
  private transitions: EasBuilderTransition[];
  public transitions$ = this._transitions$.asObservable();

  private _state$ = new ReplaySubject<EasBuilderState>(1);
  private state: EasBuilderState;
  public state$ = this._state$.asObservable();

  private secureForm: EasBuilderSecureForm;
  private user: User;

  private unsubscribe: Subject<void> = new Subject();

  init(
    wfForm: EasBuilderForm,
    model: any,
    data: any,
    wfContext: EasWorkflowContext,
    owner: User,
    user: User
  ): Observable<any>  {
    return this.initForm(wfForm, model, data, wfContext, owner, user)
  }

  initReadonly(
    wfForm: EasBuilderForm,
    model: any,
    data: any,
    wfContext: EasWorkflowContext,
    owner: User,
    user: User
  ): Observable<any>  {
    return this.initForm(wfForm, model, data, wfContext, owner, user, { readonly: true })
  }

  private initForm(
    wfForm: EasBuilderForm,
    model: any,
    data: any,
    wfContext: EasWorkflowContext,
    owner: User,
    user: User,
    options?: { readonly: boolean }
  ): Observable<any> {
    this.user = user;
    const state = this.workflow.getState(wfForm, wfContext);
    if (!state) {
      throw new Error(`Can't find state '${wfContext.state}' in the provided form!`);
    }
    this._state$.next(state);
    const formOptions = {
      user,
      owner,
      readonly: options?.readonly,
      updateOnBlur: true,
    };

    return this.workflow.getFormSecurityNg(state, owner.doc.user, user).pipe(
      switchMap(security => this.workflow.applyIndependentGuards(security, wfForm, model, user)),
      switchMap(security => this.workflow.applySecurity(wfForm, security)),
      tap(secureForm => this.secureForm = secureForm),
      switchMap(() => this.formService.buildForm(this.secureForm.group, formOptions)),
      map(group => this.easForm.createForm(group, data)),
      tap(form => this.updateEasForm(form)),
      switchMap(() => this.loadTransitions(this.easFormGroup.getFullValue())),
      tap(() => this.initialised = true),
    );
  }

  initForPreview(
    wfForm: EasBuilderForm,
    _model: any,
    data: any,
    wfContext: EasWorkflowContext,
    roles: string[],
    user: User
  ): Observable<EasFormGroup> {
    const state = this.workflow.getState(wfForm, wfContext);
    if (!state) {
      throw new Error(`Can't find state '${wfContext.state}' in the provided form!`);
    }

    // Pass dummy data to the form building infrastructure
    const options = { 'user': user, 'owner': user };

    return of(this.workflow.getFormSecurity(state, roles)).pipe(
      // switchMap(security => this.workflow.applyIndependentGuards(security, wfForm, model, user)),
      switchMap(security => this.workflow.applySecurity(wfForm, security, FieldPermission.HIDDEN)),
      tap(secureForm => this.secureForm = secureForm),
      switchMap(() => this.formService.buildForm(this.secureForm.group, options)),
      map(group => this.easForm.createForm(group, data)),
      tap(form => this.updateEasForm(form)),
      switchMap(_value => {
        this.updateTransitions(this.secureForm.transitions);
        return of(this.easFormGroup);
      }),
      tap(() => this.initialised = true),
    );
  }

  getTransitions(
    wfForm: EasBuilderForm,
    model: any,
    wfContext: EasWorkflowContext,
    owner: string,
    user: User
  ): Observable<EasBuilderTransition[]> {
    this.user = user;
    const state = this.workflow.getState(wfForm, wfContext);
    if (!state) {
      throw new Error(`Can't find state '${wfContext.state}' in the provided form!`);
    }
    this._state$.next(state);

    return this.workflow.getFormSecurityNg(state, owner, user).pipe(
      switchMap(security => this.workflow.applyIndependentGuards(security, wfForm, model, user)),
      switchMap(security => this.workflow.applySecurity(wfForm, security)),
      map(secureForm => secureForm.transitions)
    );
  }

  updateEasForm(form: EasFormGroup) {
    this.easFormGroup = form;
    this._easFormGroup$.next(form);
  }

  updateTransitions(transitions: EasBuilderTransition[]) {
    this.transitions = transitions;
    this._transitions$.next(transitions);
  }

  updateState(state: EasBuilderState) {
    this.state = state;
    this._state$.next(state);
  }

  loadTransitions(value: any) {
    return this.workflow.applyDependentGuards(this.secureForm, value, this.user).pipe(
      tap(transitions => this.updateTransitions(transitions)),
    );
  }

  watchTransitions() {
    this.unsubscribe.next();
    this.easFormGroup.control.valueChanges.pipe(
      switchMap(value => this.loadTransitions(value)),
      takeUntil(this.unsubscribe),
    ).subscribe();
  }

  checkInitialised() {
    if (!this.initialised) {
      throw new Error('Form builder state is not yet initialised');
    }
  }

  get snapshot(): EasFormWFGroup {
    this.checkInitialised();

    return {
      group: this.easFormGroup,
      transitions: this.transitions,
      state: this.state,
    };
  }

  getValue(): any {
    this.checkInitialised();
    return this.easFormGroup.getValue();
  }

  collectTransitionData(transition: EasBuilderTransition, data: any) {
    return this.workflow.collectTransitionData(transition, data);
  }

  canDelete() {
    return this.secureForm && this.secureForm.canDelete;
  }

  ngOnDestroy() {
    console.log('Destroying builder state');
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
